import { useCallback, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { updateDoorSettings, type DoorSettings } from "../../lib/api";
import { celebrate, type Celebrate } from "../../lib/celebrate";
import { meQuery, qk } from "../../lib/queries";
import { useSessionExpired } from "../../lib/session";
import { Button, LinkButton } from "../ui/button";
import { Sheet } from "../shell/surfaces";
import { useShellUi } from "../shell/state";

/**
 * EVERY "PAUSED" IS A WAY BACK UP (owner item 13, 2026-08-27).
 *
 * The word appeared in four places — the pinned desk row's chip, the rail's own
 * desk card, the settings index beside "Your door", and the door page's own Door
 * row — and in all four it was a readout. The one control that undid it sat at
 * the very bottom of the door page, under the bio, the delivery settings, the
 * whole House Rules editor and the blocked list; an owner who saw "Paused" and
 * wanted it not to be had to go and find it.
 *
 * So the word itself is the control now, wherever it is printed, and it asks
 * once before it acts. ONE component asks, and it is a shell sheet rather than
 * four local dialogs: a sheet joins the shell's escape cascade (`state.tsx`) and
 * is mounted once, so the question is the same question and closes the same way
 * whichever of the four markers opened it.
 *
 * THE CEREMONY IS STILL ASYMMETRIC (door-page.tsx §5). Taking a door down means
 * typing the handle out; putting it back up is one question with one button,
 * because nobody regrets reopening. This is a confirm, not a ceremony — it
 * exists so that a chip in a list cannot change the state of somebody's door on
 * a mis-tap, and for no other reason.
 */

export const REOPEN_QUESTION = "Put the door back up?";
export const REOPEN_NOTE =
  "Nob starts answering it again straight away. Your rules, your threads and your plan are " +
  "where you left them.";
export const REOPEN_CONFIRM_LABEL = "Put it back up";
export const REOPEN_KEEP_LABEL = "Leave it down";
export const REOPEN_FAILED = "Nob is not answering. Try that again.";

/**
 * The fourth moment this product makes something of, written once.
 *
 * The door page's own reopen button raises the same event from its own save
 * path, and two copies of a celebration are how the two come to say different
 * things about the same event.
 */
export const DOOR_BACK_UP: Omit<Celebrate, "id"> = {
  kind: "door",
  stamp: "Open",
  title: "Your door is back up.",
  body: "Nob is answering it again. Everything you had is where you left it.",
};

/**
 * Whatever prints the word, wrapped in the control that undoes it.
 *
 * It renders a plain `button` around its children and nothing else: the chip
 * keeps its `data-chip` skin, the settings value keeps its size, and the only
 * thing this adds is that pressing them asks the question. Callers that already
 * sit inside a pressable row must NOT use this — a button inside a button is not
 * HTML, and the row itself is the marker there (`surfaces.tsx`'s `Row`).
 */
export function PausedMarker({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  const ui = useShellUi();
  return (
    <button
      type="button"
      aria-label={REOPEN_QUESTION}
      onClick={(e) => {
        // The chip sits inside a conversation row that selects on click, and
        // reopening a door is not "read this thread".
        e.stopPropagation();
        ui.openSheet("reopen");
      }}
      className={`cursor-pointer focus-visible:outline-none focus-visible:ring-2
                  focus-visible:ring-focus/70 ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * The question, once, mounted by the shell.
 *
 * It owns the write rather than borrowing the door page's: the four markers are
 * on surfaces that have no door page under them, and threading a callback from
 * `app-shell` down to a chip in a list row would be a second name for one act.
 * The write is the same PATCH the door page sends, with the same two
 * invalidations — the settings the owner reads, and the public card a stranger
 * reads, which exists only for a live door.
 */
export function ReopenDoorSheet() {
  const ui = useShellUi();
  const queryClient = useQueryClient();
  const expireSession = useSessionExpired();
  const { data: me } = useQuery(meQuery());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reopen = useCallback(async () => {
    setBusy(true);
    setError(null);
    const r = await updateDoorSettings({ status: "live" });
    setBusy(false);
    if (!r.ok) {
      if (r.code === "UNAUTHENTICATED") return expireSession();
      return setError(REOPEN_FAILED);
    }
    queryClient.setQueryData<DoorSettings>(qk.doorSettings(), r.settings);
    void queryClient.invalidateQueries({ queryKey: qk.doorSettings() });
    const handle = me?.xHandle ?? null;
    if (handle !== null) void queryClient.invalidateQueries({ queryKey: qk.door(handle) });
    celebrate(DOOR_BACK_UP);
    ui.closeSheet();
  }, [expireSession, me, queryClient, ui]);

  return (
    <Sheet title={REOPEN_QUESTION} lede={REOPEN_NOTE} onClose={ui.closeSheet}>
      {error && (
        <p className="mb-3 text-sm text-danger" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" disabled={busy} onClick={() => void reopen()} className="h-10 px-4">
          {REOPEN_CONFIRM_LABEL}
        </Button>
        <LinkButton type="button" disabled={busy} onClick={ui.closeSheet}>
          {REOPEN_KEEP_LABEL}
        </LinkButton>
      </div>
    </Sheet>
  );
}
